import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

export type Plan = 'free' | 'pro' | 'premium' | 'expired';
export type SubStatus = 'active' | 'trial' | 'grace' | 'expired' | 'cancelled';

export type PlanFeature =
  | 'auto_sync'
  | 'manual_trade_unlimited'
  | 'analytics'
  | 'portfolio_analytics'
  | 'ai_assistant'
  | 'score_dashboard'
  | 'early_access';

const PLAN_FEATURES: Record<Plan, PlanFeature[]> = {
  expired: [],
  free: ['auto_sync', 'manual_trade_unlimited', 'analytics', 'portfolio_analytics'],
  pro: ['auto_sync', 'manual_trade_unlimited', 'analytics', 'portfolio_analytics', 'ai_assistant'],
  premium: ['auto_sync', 'manual_trade_unlimited', 'analytics', 'portfolio_analytics', 'ai_assistant', 'score_dashboard', 'early_access'],
};

export interface Subscription {
  plan: Plan;
  effective_plan: Plan;
  status: SubStatus;
  billing_cycle: 'monthly' | 'yearly';
  trial_plan: Plan | null;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  payment_provider: string | null;
  cancelled_at: string | null;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  effectivePlan: Plan;
  isLoading: boolean;
  canUse: (feature: PlanFeature) => boolean;
  isAtLeast: (plan: Plan) => boolean;
  refresh: () => Promise<void>;
}

const PLAN_ORDER: Plan[] = ['expired', 'free', 'pro', 'premium'];

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  effectivePlan: 'free',
  isLoading: false,
  canUse: () => false,
  isAtLeast: () => false,
  refresh: async () => {},
});

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(() => {
    const saved = localStorage.getItem('subscription_data');
    return saved ? JSON.parse(saved) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscription`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.status === 401) {
        console.warn('[SubscriptionContext] Unauthorized. Clearing token...');
        localStorage.removeItem('auth_token');
        const publicPaths = ['/', '/pricing', '/register', '/login', '/about', '/contact', '/privacy', '/terms', '/faq', '/changelog', '/verify-email'];
        const path = window.location.pathname.replace(/\/$/, '') || '/';
        const isPublic = publicPaths.includes(path);
        if (!isPublic) {
          console.warn('[SubscriptionContext] Redirecting to login/register from private route...');
          window.location.href = '/register';
        }
        return;
      }
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSubscription(data.data as Subscription);
          localStorage.setItem('subscription_data', JSON.stringify(data.data));
        }
      }
    } catch (err) {
      console.warn('[SubscriptionContext] Failed to fetch subscription:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) refresh();
  }, [refresh]);

  const effectivePlan: Plan = subscription?.effective_plan ?? 'free';

  const canUse = (feature: PlanFeature): boolean =>
    PLAN_FEATURES[effectivePlan]?.includes(feature) ?? false;

  const isAtLeast = (plan: Plan): boolean =>
    PLAN_ORDER.indexOf(effectivePlan) >= PLAN_ORDER.indexOf(plan);

  return (
    <SubscriptionContext.Provider value={{ subscription, effectivePlan, isLoading, canUse, isAtLeast, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
